/**
 * @file capture/durabilityProbe.ts
 * @brief Verify IndexedDB can hold boardroom drafts before capture starts.
 *
 * @details
 * Private / strict modes and full disk quotas break crash recovery. Probe
 * write → read → delete; block Slide-to-Start with a clear message when it
 * fails (Phase B exit criterion).
 *
 * @author DES
 * @date 28-07-2026
 */

const PROBE_DB = "verblizr-boardroom-probe";
const PROBE_STORE = "probe";

export type DurabilityProbeResult =
  | { ok: true }
  | { ok: false; error: string };

function openProbeDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(PROBE_DB, 1);
    req.onerror = () =>
      reject(req.error ?? new Error("IndexedDB open failed"));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(PROBE_STORE)) {
        db.createObjectStore(PROBE_STORE, { keyPath: "id" });
      }
    };
  });
}

function describeIdbFailure(err: unknown): string {
  const name =
    err && typeof err === "object" && "name" in err
      ? String((err as { name: string }).name)
      : "";
  if (name === "QuotaExceededError") {
    return "This device is out of storage for meeting drafts. Free some disk space, then try again.";
  }
  if (name === "InvalidStateError" || name === "UnknownError") {
    return "Local draft storage isn’t available (private / strict browsing?). Use a normal window so recordings survive refresh.";
  }
  if (typeof indexedDB === "undefined") {
    return "This browser has no IndexedDB — drafts can’t survive a refresh. Use Chrome, Edge, or Safari.";
  }
  return err instanceof Error
    ? err.message
    : "Could not verify local draft storage.";
}

/**
 * Round-trip a tiny record. Safe to call from setup before navigate.
 */
export async function probeDurableStorage(): Promise<DurabilityProbeResult> {
  if (typeof indexedDB === "undefined") {
    return {
      ok: false,
      error:
        "This browser has no IndexedDB — drafts can’t survive a refresh. Use Chrome, Edge, or Safari.",
    };
  }

  let db: IDBDatabase | null = null;
  try {
    db = await openProbeDb();
    const id = `probe_${Date.now()}`;
    const payload = { id, t: Date.now(), blob: new Blob(["vz"]) };

    await new Promise<void>((resolve, reject) => {
      const tx = db!.transaction(PROBE_STORE, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("probe write failed"));
      tx.onabort = () => reject(tx.error ?? new Error("probe write aborted"));
      tx.objectStore(PROBE_STORE).put(payload);
    });

    const read = await new Promise<unknown>((resolve, reject) => {
      const tx = db!.transaction(PROBE_STORE, "readonly");
      const req = tx.objectStore(PROBE_STORE).get(id);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    if (!read) {
      return {
        ok: false,
        error:
          "Local draft storage couldn’t be verified. Disable private browsing and try again.",
      };
    }

    await new Promise<void>((resolve, reject) => {
      const tx = db!.transaction(PROBE_STORE, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("probe delete failed"));
      tx.objectStore(PROBE_STORE).delete(id);
    });

    return { ok: true };
  } catch (err) {
    return { ok: false, error: describeIdbFailure(err) };
  } finally {
    db?.close();
    try {
      indexedDB.deleteDatabase(PROBE_DB);
    } catch {
      /* best-effort cleanup */
    }
  }
}

export function isQuotaExceeded(err: unknown): boolean {
  return (
    !!err &&
    typeof err === "object" &&
    "name" in err &&
    (err as { name: string }).name === "QuotaExceededError"
  );
}
